import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { ir } from '../lib/navegacao'
import './faixa-assinatura.css'

/**
 * A faixa que avisa da mensalidade — e nunca trava na frente do paciente.
 *
 * ─── A DECISÃO (12/ago/2026) ──────────────────────────────────────
 *
 * O mercado inteiro "trava automaticamente para pagar a anuidade" — a
 * dor #1 da pesquisa, com clínica na mão na frente do paciente. Aqui é
 * o oposto: 30 dias de AVISO (tudo funcionando), e só depois SÓ-LEITURA,
 * que ainda deixa concluir o dia, exportar e ver tudo. Nada some.
 *
 * Esta faixa é o AVISO. Quem impede o trabalho novo em SUSPENSA é o
 * banco (0039) — a tela não é a fronteira de segurança, ela é a
 * cortesia de contar antes.
 */

type Plano = { estado: string; suspende_em: string | null }

const diasAte = (iso: string | null): number | null => {
  if (!iso) return null
  // Coluna `date` volta como timestamp cheio; pega só os 10 primeiros,
  // senão `iso + 'T23:59:59'` vira Invalid Date (o mesmo do Financeiro).
  const ms = new Date(iso.slice(0, 10) + 'T23:59:59').getTime() - Date.now()
  if (Number.isNaN(ms)) return null
  return Math.max(0, Math.ceil(ms / 86_400_000))
}

export function FaixaAssinatura() {
  const [plano, setPlano] = useState<Plano | null>(null)

  useEffect(() => {
    let vivo = true
    void (async () => {
      try {
        const p = await api<Plano | null>('/v1/meu-plano')
        if (vivo) setPlano(p)
      } catch { /* sem plano visível: não é aqui que se trata */ }
    })()
    return () => { vivo = false }
  }, [])

  if (!plano) return null
  const { estado } = plano

  if (estado === 'INADIMPLENTE') {
    const dias = diasAte(plano.suspende_em)
    return (
      <div className="faixa faixa--aviso" role="status">
        <span className="faixa__txt">
          <strong>A mensalidade venceu.</strong>{' '}
          {dias !== null && dias > 0
            ? <>Você tem <strong>{dias} {dias === 1 ? 'dia' : 'dias'}</strong> antes do modo só-leitura. Tudo continua funcionando — nada será perdido.</>
            : <>Regularize para não entrar em modo só-leitura. Tudo continua funcionando — nada será perdido.</>}
        </span>
        <button className="faixa__acao" onClick={() => ir('/conta')}>Ver o plano</button>
      </div>
    )
  }

  if (estado === 'SUSPENSA') {
    return (
      <div className="faixa faixa--leitura" role="status">
        <span className="faixa__txt">
          <strong>Conta em modo só-leitura.</strong>{' '}
          Você continua vendo tudo, exportando e concluindo os atendimentos de hoje.
          Para marcar e cobrar novos, regularize a mensalidade.
        </span>
        <button className="faixa__acao" onClick={() => ir('/conta')}>Regularizar</button>
      </div>
    )
  }

  return null
}
